import { OP_STALE_MS, type OpFairView } from '@/lib/opOdds';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { makerRateForEntry } from '@/lib/pricing/fees';
import { theoreticalEntryRoi, theoreticalRoiText } from '@/lib/pricing/theoreticalRoi';
import { useFeeFor, useFeeDisplayStatus } from '@/services/hydraFees';
import { classifyLineAvailability } from '@/lib/sport/lineAvailability';
import type { BookLevel, VenueBook } from '../ValueDashboard/types';
import { bestPriceCentsForOrderSide } from '../ValueDashboard/tradeModalBook';
import { consensusDotPlacement, selectFairDotValue } from '../ValueDashboard/ladder/helpers';

export interface LadderReference {
  fair: OpFairView | null;
  yesIsAway: boolean;
  strike: number;
}

/** Shared read-only space between the outcome columns. Labels may move to
 * avoid each other; edge ticks stay at the exact price position. */
export function ReferenceRail({ reference, book, columns }: {
  reference: LadderReference;
  book: VenueBook | null;
  columns: { side: 'yes' | 'no'; asks: (BookLevel | null)[]; bids: (BookLevel | null)[] }[];
}) {
  const nowMs = useSharedNow(1000);
  const entry = useFeeFor(book?.marketTicker);
  const feeStatus = useFeeDisplayStatus(book?.marketTicker, entry);
  const { fair, yesIsAway, strike } = reference;
  const readings = columns.map(({ side, asks, bids }) => {
    const fairSide = (side === 'yes') === yesIsAway ? 'away' : 'home';
    const selected = selectFairDotValue(fair?.[fairSide]);
    const stamp = selected?.source === 'pinnacle' ? fair?.anchorUpdatedAtMs : fair?.consensusLastUpdateMs;
    const fresh = stamp != null && Number.isFinite(stamp) && stamp <= nowMs && nowMs - stamp <= OP_STALE_MS;
    const cents = selected?.tenths != null ? selected.tenths / 10 : selected?.cents ?? null;
    const placement = fresh && cents != null ? consensusDotPlacement(asks, bids, cents) : null;
    const position = placement ? (placement.kind === 'ask' ? placement.idx : asks.length + placement.idx) + placement.offsetRows : null;
    const cleanBook = book ? { ...book, [side]: book[side].filter(l => Number.isFinite(l.size) && Number.isFinite(l.priceCents) && l.priceCents > 0 && l.priceCents < 100) } : undefined;
    const result = theoreticalEntryRoi({ fair, fairSide, strike,
      priceCents: bestPriceCentsForOrderSide(cleanBook, side) ?? null,
      makerFeeRate: feeStatus === 'exact' ? makerRateForEntry(entry) : null,
      nowMs, available: classifyLineAvailability(book) === 'active' });
    const tick = position == null ? null : 40 + (position + 0.5) * 22;
    return { side, selected, stamp, fresh, cents, placement, result, tick, top: Math.max(40, (tick ?? 64) - 24) };
  });
  // Keep both labels inside the market rows and separate, even at equal fair.
  const height = 40 + Math.max(...columns.map(c => c.asks.length + c.bids.length)) * 22;
  const ordered = [...readings].sort((a,b) => a.top - b.top);
  if (ordered.length === 2) {
    ordered[1].top = Math.min(height - 54, Math.max(ordered[1].top, ordered[0].top + 56));
    ordered[0].top = Math.max(40, Math.min(ordered[0].top, ordered[1].top - 56));
  }
  return <div className="relative w-[100px] shrink-0 self-stretch font-mono text-xs" data-reference-rail="" onClick={e => e.stopPropagation()}>
    {readings.map((r, index) => <div key={r.side}>
      {r.tick != null && <span aria-hidden className="absolute w-2 border-t border-amber-400" data-reference-tick={r.side}
        style={{ top: r.tick, [index === 0 ? 'left' : 'right']: 0 }} />}
      <div className="absolute left-0 right-0 rounded border border-border/50 bg-card px-1 py-0.5 leading-4"
        style={{ top: r.top }} data-reference-floater={r.side} data-reference-source={r.fresh ? r.selected?.source : undefined}>
        <div className="font-semibold text-amber-400">{r.side.toUpperCase()} {r.fresh && r.cents != null
          ? `${r.selected?.source === 'pinnacle' ? 'P' : 'O'}${r.cents.toFixed(1)}${r.placement?.clamped === 'above' ? '▲' : r.placement?.clamped === 'below' ? '▼' : ''}` : 'Ref —'}</div>
        <div title={r.result.reason ?? 'Conditional passive-fill ROI versus Pinnacle, including entry maker fee; before rebates.'}>ROI {theoreticalRoiText(r.result)}</div>
        <div className="text-[10px] text-muted-foreground">{r.fresh && r.stamp != null ? new Date(r.stamp).toISOString().slice(11, 19) + ' UTC' : 'stale/absent'}</div>
      </div>
    </div>)}
  </div>;
}
